import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Phone, Globe, MapPin, Mail, Baby, DollarSign, CheckCircle2, Clock, Star, BadgeCheck, Radar, List } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import Navbar from "../components/Navbar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const STATES = ["", "NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];
const chip = "inline-flex items-center gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 text-sm font-semibold";

function SaveStar({ saved, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} title={saved ? "Remove bookmark" : "Save centre"}
      className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center transition ${saved ? "text-[#FFC02D]" : "text-slate-300 hover:text-[#FFC02D]"}`}>
      <Star className={`h-5 w-5 ${saved ? "fill-[#FFC02D]" : ""}`} />
    </button>
  );
}

function ManagedCard({ c, onSave, pinned }) {
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address || `${c.name} ${c.suburb} ${c.state}`)}`;
  return (
    <div className={`bg-white rounded-3xl border shadow-lg p-5 flex flex-col ${pinned ? "border-[#2EC4B6]" : "border-amber-100/60"}`} data-testid={`centre-${c.id}`}>
      {pinned && <div className="text-xs font-bold text-[#2EC4B6] flex items-center gap-1 mb-2" data-testid={`partner-badge-${c.id}`}><BadgeCheck className="h-3.5 w-3.5" /> Bubz + listed centre</div>}
      <div className="flex items-start gap-2">
        <h3 className="font-display font-bold text-lg text-slate-900 flex-1">{c.name}</h3>
        <SaveStar saved={c.saved} onClick={() => onSave(c)} testid={`save-centre-${c.id}`} />
        <span className="shrink-0 rounded-full bg-slate-100 text-slate-600 text-xs font-bold px-2.5 py-1">{c.state}</span>
      </div>
      <div className="text-sm text-slate-500 mt-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.suburb} {c.postcode}</div>
      <p className="text-sm text-slate-400 mt-1">{c.address}</p>
      <div className="flex flex-wrap gap-2 mt-3 text-xs">
        {c.age_ranges && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 px-2.5 py-1 font-semibold"><Baby className="h-3.5 w-3.5" /> {c.age_ranges}</span>}
        {c.fees && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 font-semibold"><DollarSign className="h-3.5 w-3.5" /> {c.fees}</span>}
        {c.vacancies
          ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Vacancies available</span>
          : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-500 px-2.5 py-1 font-semibold"><Clock className="h-3.5 w-3.5" /> Waitlist</span>}
      </div>
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
        {c.phone && <a href={`tel:${c.phone}`} data-testid={`centre-call-${c.id}`} className={chip}><Phone className="h-4 w-4" /> Call</a>}
        {c.website && <a href={c.website} target="_blank" rel="noreferrer" className={chip}><Globe className="h-4 w-4" /> Website</a>}
        <a href={mapsUrl} target="_blank" rel="noreferrer" className={chip}><MapPin className="h-4 w-4" /> Map</a>
        {c.email && <a href={`mailto:${c.email}?subject=Enquiry about ${encodeURIComponent(c.name)}`} data-testid={`centre-info-${c.id}`} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#2EC4B6] hover:bg-[#25a99d] text-white px-3 py-2 text-sm font-bold"><Mail className="h-4 w-4" /> Request info</a>}
      </div>
    </div>
  );
}

function LiveCard({ p, onSave }) {
  return (
    <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-5 flex flex-col" data-testid={`live-centre-${p.place_id}`}>
      <div className="flex items-start gap-2">
        <h3 className="font-display font-bold text-lg text-slate-900 flex-1">{p.name}</h3>
        <SaveStar saved={p.saved} onClick={() => onSave(p)} testid={`save-live-${p.place_id}`} />
      </div>
      <div className="text-sm text-slate-500 mt-1 flex items-start gap-1"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {p.address}</div>
      {p.rating != null && (
        <div className="mt-3 text-xs"><span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 font-semibold"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {p.rating.toFixed(1)}{p.rating_count ? ` (${p.rating_count})` : ""} on Google</span></div>
      )}
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
        {p.phone && <a href={`tel:${p.phone}`} className={chip} data-testid={`live-call-${p.place_id}`}><Phone className="h-4 w-4" /> Call</a>}
        {p.website && <a href={p.website} target="_blank" rel="noreferrer" className={chip}><Globe className="h-4 w-4" /> Website</a>}
        {p.maps_url && <a href={p.maps_url} target="_blank" rel="noreferrer" className={chip}><MapPin className="h-4 w-4" /> Google Maps</a>}
      </div>
    </div>
  );
}

function ModeToggle({ mode, setMode }) {
  const btn = (v, label, Icon) => (
    <button onClick={() => setMode(v)} data-testid={`childcare-mode-${v}`}
      className={`rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 transition-colors ${mode === v ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
  return <div className="inline-flex rounded-full bg-slate-100 p-1 mb-4">{btn("list", "Our list", List)}{btn("live", "Live search", Radar)}</div>;
}

function LiveSearch({ savedOnly }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async (pageToken) => {
    if (q.trim().length < 2) return;
    setLoading(true); setError("");
    try {
      const { data: d } = await api.get("/childcare/live", { params: { q, page_token: pageToken || "" } });
      setData((prev) => pageToken && prev ? { ...d, managed: prev.managed, results: [...prev.results, ...d.results] } : d);
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || "Live search is unavailable right now"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!savedOnly) return;
    setLoading(true); setError("");
    api.get("/childcare/live-saved").then(({ data: d }) => setSaved(d))
      .catch((err) => setError(formatApiError(err.response?.data?.detail) || "Could not load saved centres"))
      .finally(() => setLoading(false));
  }, [savedOnly]);

  const saveLive = async (p) => {
    const { data: r } = await api.post(`/childcare/live/${p.place_id}/save`);
    const upd = (list) => list.map((x) => x.place_id === p.place_id ? { ...x, saved: r.saved } : x);
    setData((d) => d && { ...d, results: upd(d.results) });
    setSaved((s) => r.saved ? upd(s) : s.filter((x) => x.place_id !== p.place_id));
  };
  const saveManaged = async (c) => {
    const { data: r } = await api.post(`/childcare/${c.id}/save`);
    setData((d) => ({ ...d, managed: d.managed.map((x) => x.id === c.id ? { ...x, saved: r.saved } : x) }));
  };

  if (savedOnly) {
    return (
      <div data-testid="live-saved">
        {loading ? <p className="text-center text-slate-400 py-10">Loading saved centres…</p>
          : error ? <p className="text-center text-slate-400 py-10" data-testid="live-error">{error}</p>
          : saved.length === 0 ? <p className="text-center text-slate-400 py-10" data-testid="live-saved-empty">No saved live centres yet.</p>
          : <div className="grid sm:grid-cols-2 gap-4">{saved.map((p) => <LiveCard key={p.place_id} p={p} onSave={saveLive} />)}</div>}
      </div>
    );
  }

  return (
    <div data-testid="live-search">
      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-4 mb-6 flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="live-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suburb or postcode, e.g. Parramatta NSW or 3000" className="rounded-full h-11 pl-10" />
        </div>
        <Button type="submit" disabled={loading || q.trim().length < 2} data-testid="live-search-btn" className="rounded-full h-11 px-6 font-bold bg-[#2EC4B6] hover:bg-[#25a99d]">
          {loading ? "Searching…" : "Search"}
        </Button>
      </form>
      {error && <p className="text-center text-slate-500 py-6" data-testid="live-error">{error}</p>}
      {!data && !error && <p className="text-center text-slate-400 py-10" data-testid="live-hint">Search any Australian suburb or postcode to see real centres near you.</p>}
      {data && (
        <>
          {data.managed.length + data.results.length === 0 && <p className="text-center text-slate-400 py-10" data-testid="live-empty">No centres found there. Try a nearby suburb.</p>}
          <div className="grid sm:grid-cols-2 gap-4" data-testid="live-results">
            {data.managed.map((c) => <ManagedCard key={c.id} c={c} onSave={saveManaged} pinned />)}
            {data.results.map((p) => <LiveCard key={p.place_id} p={p} onSave={saveLive} />)}
          </div>
          {data.next_page_token && (
            <div className="text-center mt-6">
              <Button variant="outline" disabled={loading} onClick={() => run(data.next_page_token)} data-testid="live-more-btn" className="rounded-full font-bold">Show more centres</Button>
            </div>
          )}
          <p className="text-xs text-slate-400 text-right mt-4" data-testid="google-attribution">Results powered by Google</p>
        </>
      )}
    </div>
  );
}

function OurList({ savedOnly }) {
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [vacOnly, setVacOnly] = useState(false);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = savedOnly ? await api.get("/childcare/saved")
        : await api.get("/childcare", { params: { q, state, vacancies: vacOnly ? "true" : "" } });
      setCentres(data);
    } finally { setLoading(false); }
  }, [q, state, vacOnly, savedOnly]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggleSave = async (c) => {
    const { data } = await api.post(`/childcare/${c.id}/save`);
    if (savedOnly && !data.saved) setCentres((cs) => cs.filter((x) => x.id !== c.id));
    else setCentres((cs) => cs.map((x) => x.id === c.id ? { ...x, saved: data.saved } : x));
  };

  return (
    <>
      {!savedOnly && (
        <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-4 mb-6 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="childcare-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, suburb or postcode…" className="rounded-full h-11 pl-10" />
          </div>
          <select data-testid="childcare-state" value={state} onChange={(e) => setState(e.target.value)}
            className="rounded-full h-11 px-4 border border-slate-200 bg-white text-sm font-semibold text-slate-600">
            {STATES.map((s) => <option key={s} value={s}>{s || "All states"}</option>)}
          </select>
          <button onClick={() => setVacOnly((v) => !v)} data-testid="childcare-vacancies-toggle"
            className={`rounded-full h-11 px-4 text-sm font-bold border-2 transition ${vacOnly ? "bg-[#2EC4B6] text-white border-transparent" : "bg-white text-slate-600 border-slate-200"}`}>
            Vacancies only
          </button>
        </div>
      )}
      {loading ? <p className="text-center text-slate-400 py-10">Loading centres…</p>
        : centres.length === 0 ? <p className="text-center text-slate-400 py-10" data-testid="childcare-empty">No centres match your search.</p>
        : <div className="grid sm:grid-cols-2 gap-4" data-testid="childcare-list">{centres.map((c) => <ManagedCard key={c.id} c={c} onSave={toggleSave} />)}</div>}
    </>
  );
}

export default function Childcare() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("list");
  const [savedOnly, setSavedOnly] = useState(false);

  return (
    <div className="min-h-screen bg-[#FFFDF7] pb-16">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate("/parent")} data-testid="back-to-parent-btn"
          className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-800 font-semibold text-sm mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to Parent Zone
        </button>
        <div className="flex items-center gap-2 mb-1">
          <h1 className="font-display font-bold text-3xl text-slate-900">Childcare Directory</h1>
          <Baby className="h-6 w-6 text-[#2EC4B6]" />
        </div>
        <p className="text-slate-500 mb-5">Browse our listed centres, or search live for any centre across Australia.</p>
        <div className="flex flex-wrap items-start gap-3">
          <ModeToggle mode={mode} setMode={setMode} />
          <button onClick={() => setSavedOnly((v) => !v)} data-testid="childcare-saved-toggle"
            className={`rounded-full h-11 px-4 text-sm font-bold border-2 transition inline-flex items-center gap-1.5 ${savedOnly ? "bg-[#FFC02D] text-white border-transparent" : "bg-white text-slate-600 border-slate-200"}`}>
            <Star className={`h-4 w-4 ${savedOnly ? "fill-white" : ""}`} /> Saved
          </button>
        </div>
        {mode === "list" ? <OurList savedOnly={savedOnly} /> : <LiveSearch savedOnly={savedOnly} />}
      </div>
    </div>
  );
}
